import { getLogger, type Logger } from "@expert/logging";
import { Flex } from "@mantine/core";
import { useMemo } from "react";
import { withErrorBoundary } from "react-error-boundary";

import type { ExpertUniversitySimulatedVoiceTask } from "@/expert-university";

import { CallControlsManager } from "@/call-controls";
import { useIsGlowBarPacingActive } from "@/call-controls/hooks/useIsGlowBarPacingActive";
import { useShouldShowCallControls } from "@/call-controls/hooks/useShouldShowCallControls";
import { UI_ORCHESTRATION_COMPONENT } from "@/expert-hub/data/expertHubErrorMetric";
import { ExpertUniversityExperience } from "@/expert-university/components/ExpertUniversityExperience";
import { usePlatformGuideManager } from "@/guides";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { DeprecatedSessionSummary, SessionSummary } from "@/session-summary";
import { createFallbackRender, trackedBoundaryProps } from "@/shared-utils";
import { Sidecar } from "@/sidecar/Sidecar";
import { RedesignedTranscript } from "@/transcript/redesigned/RedesignedTranscript";
import { WarmHandoffOverlay } from "@/warm-handoff";

import { useEndOfCallSummary } from "./hooks/useEndOfCallSummary";
import { useLiveVoiceTask } from "./hooks/useLiveVoiceTask";
import { useWorkspaceHomeState } from "./redesigned-layout/hooks/useWorkspaceHomeState";
import { WorkspaceMainSlot, WorkspaceRightSlot } from "./redesigned-layout/WorkspaceHomeSlots";
import { WorkspaceLayoutV2 } from "./redesigned-layout/WorkspaceLayoutV2";

interface Props {
    activeExpertUniversityTask: ExpertUniversitySimulatedVoiceTask | null;
    /** Partner-level toggle for whether the customer panel exists at all. */
    customerPanelEnabled: boolean;
    customerSession: boolean;
    hasActiveVoiceTask: boolean;
    logger: Logger;
    sessionId: string;
    showExpertUniversity: undefined | boolean;
    startedTimestamp: number;
}

const transcriptCrashLogger = getLogger({ module: "RedesignedTranscript" });
const warmHandoffCrashLogger = getLogger({ module: "WarmHandoffOverlay" });
const WarmHandoffOverlayHandled = withErrorBoundary(
    WarmHandoffOverlay,
    trackedBoundaryProps(warmHandoffCrashLogger, UI_ORCHESTRATION_COMPONENT),
);
const TranscriptHandled = withErrorBoundary(
    RedesignedTranscript,
    trackedBoundaryProps(transcriptCrashLogger, UI_ORCHESTRATION_COMPONENT),
);

/**
 * Redesigned voice workspace content (gated by `isVoiceWorkspaceRedesignV2Enabled`): the redesigned
 * body plus the Expert Hub. Off-call, the hub fills the main area and the assist / customer panel
 * slides into the off-call right panel. It reuses every shared body component and adds the Expert Hub.
 * It has no left widget sidebar — off-call the Expert Hub owns that space, and on-call it never showed.
 */
export function VoiceWorkspaceContentRedesignedV2({
    activeExpertUniversityTask,
    customerPanelEnabled,
    customerSession,
    hasActiveVoiceTask,
    logger,
    sessionId,
    showExpertUniversity,
    startedTimestamp,
}: Props) {
    const { activeVoiceTask, hasLiveVoiceTask } = useLiveVoiceTask(hasActiveVoiceTask);

    const callControlsLogger = useLogger({ module: "VoiceWorkspaceContentRedesignedV2" });
    const CallControlsManagerHandled = useMemo(
        () => withErrorBoundary(CallControlsManager, { fallbackRender: createFallbackRender(callControlsLogger) }),
        [callControlsLogger],
    );

    const SessionSummaryHandled = useMemo(
        () => withErrorBoundary(SessionSummary, { fallbackRender: createFallbackRender(logger) }),
        [logger],
    );

    const DeprecatedSessionSummaryHandled = useMemo(
        () => withErrorBoundary(DeprecatedSessionSummary, { fallbackRender: createFallbackRender(logger) }),
        [logger],
    );

    const { areGuidesAvailable, guide, guideToolboxCommand, isGuideOpen, closeGuide } = usePlatformGuideManager({
        isCustomerSession: customerSession,
        sessionId,
        logger,
    });
    // Take open() from guideToolboxCommand (not the raw openGuide) so the redesign keeps its
    // error-toast fallback when no guide is available to launch.
    const { open: openGuide } = guideToolboxCommand;

    const { displaySummary, showUpdatedFlow } = useEndOfCallSummary({ customerSession });

    const expertUniversityActive = Boolean(showExpertUniversity);

    // Off-call = no live call/customer session and not in an Expert University takeover. A training task
    // is neither a live call nor a customer session, so it also lands here — the Expert Hub is the home
    // for that state (v2 no longer renders the legacy Mission Control training modal; see git history).
    const isOffCall = !hasLiveVoiceTask && !customerSession && !expertUniversityActive;

    // Show the leftTop call-controls panel only when the bar has something to render
    // (see useShouldShowCallControls) and we're not in Expert University. Without this gate the
    // panel opens around an empty CallControlsManager. Kept off-call too so the expert can still
    // start an outbound call from the Expert Hub.
    const shouldShowCallControls = useShouldShowCallControls();
    const leftTopOpen = !expertUniversityActive && shouldShowCallControls;

    const isGlowBarActive = useIsGlowBarPacingActive();

    const { showExpertHub, rightTopOpen, rightTopSize, rightTopMin, rightTopMax } = useWorkspaceHomeState({
        isOffCall,
        baseRightTopOpen: !expertUniversityActive,
    });

    const topbarContent = (
        <Flex pos="absolute" inset={0}>
            <CallControlsManagerHandled voiceTask={hasLiveVoiceTask ? activeVoiceTask : null} isCompact />
        </Flex>
    );

    const leftMainContent = (
        <Flex pos="absolute" inset={0}>
            {expertUniversityActive ? (
                <ExpertUniversityExperience />
            ) : (
                // Keyed by session, not task: a callback is a new task in the same session and must keep
                // the transcript from the call before it.
                <TranscriptHandled key={sessionId} taskStartTimeInMs={startedTimestamp} />
            )}
        </Flex>
    );

    // Mirrors the legacy WorkspaceContentRightSidebarSlot gate: show the customer panel for a real
    // session only when the partner has it enabled, but always during an Expert University training
    // sim (which uses simulated data, so the partner toggle doesn't apply).
    const showCustomerPanel = (customerSession && customerPanelEnabled) || Boolean(activeExpertUniversityTask);

    const rightMainContent = (
        <Sidecar
            customerSession={showCustomerPanel}
            openGuide={openGuide}
            closeGuide={closeGuide}
            guide={guide}
            areGuidesAvailable={areGuidesAvailable}
            isGuideOpen={isGuideOpen}
        />
    );

    return (
        <>
            <WorkspaceLayoutV2
                leftTop={topbarContent}
                leftTopOpen={leftTopOpen}
                leftTopFullWidth={isGlowBarActive}
                leftTopHeight={isGlowBarActive ? "80px" : undefined}
                leftBottom={<WorkspaceMainSlot showExpertHub={showExpertHub} onCallContent={leftMainContent} />}
                rightTop={<WorkspaceRightSlot showExpertHub={showExpertHub} onCallContent={rightMainContent} />}
                rightTopOpen={rightTopOpen}
                rightTopSize={rightTopSize}
                rightTopMin={rightTopMin}
                rightTopMax={rightTopMax}
                persistId="voice"
                showExpertHub={showExpertHub}
            />
            {displaySummary && (showUpdatedFlow ? <SessionSummaryHandled /> : <DeprecatedSessionSummaryHandled />)}
            <WarmHandoffOverlayHandled />
        </>
    );
}
